// RamsDraftPanel — Phase 4: the editable AI-generated draft (narrative
// sections + risk assessment matrix) for a Submitted/AI Draft Generated RAMS
// request. Reviewer can tweak wording/ratings before Phase 5's approval +
// branded PDF/DOCX export.
import { useEffect, useState } from "react";
import { Loader2, Sparkles, Save, RotateCcw, AlertTriangle, ChevronDown, ChevronUp, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, pollGenerateStatus } from "@/lib/api";
import RamsSupportingContextPicker from "./RamsSupportingContextPicker";

const inputCls = "w-full px-3 py-2 rounded-lg border border-[#E5E0DA] bg-white text-[12.5px]";
const fieldLabel = "block text-[10.5px] font-semibold uppercase tracking-wider text-[#9A968F] mb-0.5";

export default function RamsDraftPanel({ requestId, onChanged }) {
    const [draft, setDraft] = useState(null);
    const [reference, setReference] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [regenOpen, setRegenOpen] = useState(false);
    const [regenNote, setRegenNote] = useState("");
    const [regenBusy, setRegenBusy] = useState(false);
    const [regenDocIds, setRegenDocIds] = useState([]);
    const [openSections, setOpenSections] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: ref }] = await Promise.all([api.get("/rams/risk-matrix/reference")]);
            setReference(ref);
            const { data: d } = await api.get(`/rams/requests/${requestId}/draft`);
            setDraft(d);
        } catch (e) { setDraft(null); }
        setLoading(false);
    };
    useEffect(() => { load(); }, [requestId]);

    const updateSection = (key, content) => {
        setDraft((d) => ({ ...d, sections: d.sections.map((s) => (s.key === key ? { ...s, content } : s)) }));
        setDirty(true);
    };
    const updateRow = (id, patch) => {
        setDraft((d) => ({ ...d, risk_matrix: d.risk_matrix.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/rams/requests/${requestId}/draft`, {
                sections: draft.sections, risk_matrix: draft.risk_matrix,
            });
            setDraft(data);
            setDirty(false);
            toast.success("Draft saved");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save draft"); }
        setSaving(false);
    };

    const regenerate = async () => {
        setRegenBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/generate-draft`, { instructions: regenNote || null, supporting_document_ids: regenDocIds });
            await pollGenerateStatus(`/rams/requests/${requestId}/generate-draft/status`);
            const { data } = await api.get(`/rams/requests/${requestId}/draft`);
            setDraft(data);
            setDirty(false);
            setRegenOpen(false);
            setRegenNote("");
            toast.success("AI draft regenerated");
            onChanged?.();
        } catch (e) { toast.error(e.response ? (formatApiError(e.response?.data?.detail) || "Regeneration failed") : (e.message || "Regeneration failed")); }
        setRegenBusy(false);
    };

    if (loading) return <div className="py-6 text-center text-[#9A968F]"><Loader2 className="inline animate-spin" size={14} /></div>;
    if (!draft) return null;

    const bandColor = (band) => (reference?.bands || []).find((b) => b.label === band)?.color || "#9A968F";

    return (
        <div className="space-y-3" data-testid="rams-draft-panel">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <p className="text-[12.5px] font-bold text-[#1F1F1F]">AI Draft — revision {draft.revision}</p>
                    <p className="text-[11px] text-[#9A968F]">
                        Generated {draft.generated_at ? new Date(draft.generated_at).toLocaleString() : "—"} by {draft.generated_by_name}
                        {draft.edited_at && ` · edited by ${draft.edited_by_name}`}
                    </p>
                </div>
                <button data-testid="rams-draft-regenerate-toggle" onClick={() => setRegenOpen((o) => !o)}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0] shrink-0">
                    <RotateCcw size={12} /> Regenerate
                </button>
            </div>

            {regenOpen && (
                <div className="p-3 rounded-lg bg-[#FBFAF8] border border-[#F0EDE8] space-y-2">
                    <textarea data-testid="rams-draft-regen-instructions" rows={2} value={regenNote}
                        onChange={(e) => setRegenNote(e.target.value)}
                        placeholder="Optional extra guidance for this regeneration (e.g. 'expand the emergency procedures section')…"
                        className={inputCls} />
                    <RamsSupportingContextPicker requestId={requestId} selectedIds={regenDocIds} onChange={setRegenDocIds} />
                    <button data-testid="rams-draft-regen-confirm" onClick={regenerate} disabled={regenBusy}
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {regenBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Overwrite current draft &amp; regenerate
                    </button>
                </div>
            )}

            {draft.context_sources?.length > 0 && (
                <p data-testid="rams-draft-context-sources" className="text-[11px] text-[#9A968F] flex items-center gap-1.5 flex-wrap">
                    <FileCheck2 size={12} className="text-emerald-600 shrink-0" />
                    Context used: {draft.context_sources.map((c) => c.label).join(", ")}
                </p>
            )}
            {draft.context_skipped?.length > 0 && (
                <p data-testid="rams-draft-context-skipped" className="text-[11px] text-amber-700 flex items-center gap-1.5 flex-wrap">
                    <AlertTriangle size={12} className="shrink-0" />
                    Couldn't be read, so skipped: {draft.context_skipped.map((c) => c.label).join(", ")}
                </p>
            )}

            {draft.contamination_warnings?.length > 0 && (
                <div data-testid="rams-draft-contamination-warning" className="p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-1">
                    <p className="text-[11.5px] font-bold text-amber-800 flex items-center gap-1.5"><AlertTriangle size={13} /> Possible content contamination — please review</p>
                    {draft.contamination_warnings.map((w, i) => <p key={i} className="text-[11px] text-amber-700">{w}</p>)}
                </div>
            )}

            <div className="space-y-2">
                {draft.sections.map((s) => (
                    <div key={s.key} className="rounded-lg border border-[#E5E0DA] overflow-hidden">
                        <button onClick={() => setOpenSections((o) => ({ ...o, [s.key]: !o[s.key] }))}
                            data-testid={`rams-draft-section-toggle-${s.key}`}
                            className="w-full flex items-center justify-between px-3 py-2 text-[12.5px] font-semibold text-[#1F1F1F] bg-[#FBFAF8]">
                            {s.title}
                            {openSections[s.key] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {openSections[s.key] && (
                            <div className="px-3 py-2.5">
                                <textarea data-testid={`rams-draft-section-content-${s.key}`} rows={6} value={s.content}
                                    onChange={(e) => updateSection(s.key, e.target.value)} className={inputCls + " resize-y"} />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div>
                <p className="text-[12.5px] font-bold text-[#1F1F1F] mb-2">Risk assessment matrix</p>
                {draft.risk_matrix.length === 0 ? (
                    <p className="text-[12px] text-[#9A968F]">No hazards were flagged on the questionnaire.</p>
                ) : (
                    <div className="space-y-2">
                        {draft.risk_matrix.map((row) => (
                            <div key={row.id} data-testid={`rams-draft-risk-row-${row.hazard_key}`} className="p-3 rounded-lg border border-[#E5E0DA] space-y-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-[12.5px] font-bold text-[#1F1F1F]">{row.hazard_label}</p>
                                    <span data-testid={`rams-draft-band-${row.hazard_key}`} className="px-2 py-0.5 rounded-full text-[11px] font-semibold text-white"
                                        style={{ background: bandColor(row.band) }}>{row.band} ({row.rating})</span>
                                </div>
                                <div>
                                    <label className={fieldLabel}>Who's at risk</label>
                                    <input data-testid={`rams-draft-who-${row.hazard_key}`} value={row.who_at_risk || ""}
                                        onChange={(e) => updateRow(row.id, { who_at_risk: e.target.value })} className={inputCls} />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className={fieldLabel}>Likelihood</label>
                                        <select data-testid={`rams-draft-likelihood-${row.hazard_key}`} value={row.likelihood}
                                            onChange={(e) => updateRow(row.id, { likelihood: Number(e.target.value) })} className={inputCls}>
                                            {(reference?.likelihood || []).map((o) => <option key={o.score} value={o.score}>{o.score} — {o.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={fieldLabel}>Severity</label>
                                        <select data-testid={`rams-draft-severity-${row.hazard_key}`} value={row.severity}
                                            onChange={(e) => updateRow(row.id, { severity: Number(e.target.value) })} className={inputCls}>
                                            {(reference?.severity || []).map((o) => <option key={o.score} value={o.score}>{o.score} — {o.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className={fieldLabel}>Existing controls</label>
                                    <textarea data-testid={`rams-draft-controls-${row.hazard_key}`} rows={2} value={row.existing_controls || ""}
                                        onChange={(e) => updateRow(row.id, { existing_controls: e.target.value })} className={inputCls + " resize-none"} />
                                </div>
                                <div>
                                    <label className={fieldLabel}>Additional controls</label>
                                    <textarea data-testid={`rams-draft-additional-${row.hazard_key}`} rows={2} value={row.additional_controls || ""}
                                        onChange={(e) => updateRow(row.id, { additional_controls: e.target.value })} className={inputCls + " resize-none"} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {dirty && (
                <button data-testid="rams-draft-save" onClick={save} disabled={saving}
                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
                    style={{ background: "var(--brand-accent, #C75A1B)" }}>
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save changes
                </button>
            )}
        </div>
    );
}
